import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Badge, Button, Tabs, Tab } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../../../utils/axiosConfig';
import { useAuth } from '../../../contexts/AuthContext';
import './ViewVehicle.css';

const ViewVehicle = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { hasRole } = useAuth();

    const [vehicle, setVehicle] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchVehicle = async () => {
            try {
                const response = await axiosInstance.get(`/vehicles/${id}`);
                // Handle both response structures: response.data.data or response.data
                const vehicleData = response.data.data || response.data;
                console.log('Vehicle data:', vehicleData);
                setVehicle(vehicleData);
            } catch (error) {
                console.error('Error fetching vehicle:', error);
                toast.error('Failed to load vehicle data');
            } finally {
                setLoading(false);
            }
        };

        fetchVehicle();
    }, [id]);

    const getStatusBadge = (status) => {
        const statusMapping = {
            approved: { text: 'Approved', variant: 'success' },
            pending: { text: 'Pending', variant: 'warning' },
            rejected: { text: 'Rejected', variant: 'danger' },
            rejected_for_edit: { text: 'Needs Revision', variant: 'info' }
        };
        const { text, variant } = statusMapping[status] || { text: status, variant: 'secondary' };
        return <Badge bg={variant} className="fs-6">{text}</Badge>;
    };

    if (loading) {
        return (
            <Container className="mt-5">
                <div className="text-center">Loading...</div>
            </Container>
        );
    }

    if (!vehicle) {
        return (
            <Container className="mt-5">
                <div className="text-center">Vehicle not found</div>
            </Container>
        );
    }

    return (
        <Container fluid className="mt-4">

            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <Button variant="outline-secondary" onClick={() => navigate('/vehicle-management')} className="me-2">
                        Back to List
                    </Button>
                    <h3 className="d-inline">{vehicle.title_en} {getStatusBadge(vehicle.status)}</h3>
                </div>
                <div>
                    <Link to={`/vehicle-management/${id}/colors`}>
                        <Button variant="outline-primary" className="me-2">Manage Colors</Button>
                    </Link>
                    <Link to={`/vehicle-management/${id}/variants`}>
                        <Button variant="outline-primary">Manage Variants</Button>
                    </Link>
                </div>
            </div>

            <Tabs defaultActiveKey="details" className="mb-3">
                <Tab eventKey="details" title="Vehicle Details">
                    <Row>
                        <Col md={6}>
                            <Card className="mb-4">
                                <Card.Header>Basic Information</Card.Header>
                                <Card.Body>
                                    <Row className="mb-2">
                                        <Col md={4}><strong>Vehicle ID:</strong></Col>
                                        <Col md={8}>{vehicle.vehicle_id}</Col>
                                    </Row>
                                    <Row className="mb-2">
                                        <Col md={4}><strong>Title (EN):</strong></Col>
                                        <Col md={8}>{vehicle.title_en}</Col>
                                    </Row>
                                    <Row className="mb-2">
                                        <Col md={4}><strong>Title (AR):</strong></Col>
                                        <Col md={8}>{vehicle.title_ar}</Col>
                                    </Row>
                                    <Row className="mb-2">
                                        <Col md={4}><strong>Category:</strong></Col>
                                        <Col md={8}>{vehicle.category}</Col>
                                    </Row>
                                    <Row className="mb-2">
                                        <Col md={4}><strong>Slug:</strong></Col>
                                        <Col md={8}>{vehicle.slug}</Col>
                                    </Row>
                                    <Row className="mb-2">
                                        <Col md={4}><strong>Active:</strong></Col>
                                        <Col md={8}>
                                            <Badge bg={vehicle.is_active ? 'success' : 'secondary'}>
                                                {vehicle.is_active ? 'Yes' : 'No'}
                                            </Badge>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={6}>
                            <Card className="mb-4">
                                <Card.Header>Status & Flags</Card.Header>
                                <Card.Body>
                                    <Row className="mb-2">
                                        <Col md={6}><strong>Discontinued:</strong></Col>
                                        <Col md={6}>{vehicle.discontinued ? 'Yes' : 'No'}</Col>
                                    </Row>
                                    <Row className="mb-2">
                                        <Col md={6}><strong>Coming Soon:</strong></Col>
                                        <Col md={6}>{vehicle.coming_soon ? 'Yes' : 'No'}</Col>
                                    </Row>
                                    <Row className="mb-2">
                                        <Col md={6}><strong>Show on Spare Parts:</strong></Col>
                                        <Col md={6}>{vehicle.show_on_spare_parts_page ? 'Yes' : 'No'}</Col>
                                    </Row>
                                    <Row className="mb-2">
                                        <Col md={6}><strong>Maintenance Only:</strong></Col>
                                        <Col md={6}>{vehicle.maintenance_only ? 'Yes' : 'No'}</Col>
                                    </Row>
                                    <Row className="mb-2">
                                        <Col md={6}><strong>Sort Order:</strong></Col>
                                        <Col md={6}>{vehicle.sort_order}</Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <Card className="mb-4">
                                <Card.Header>Description</Card.Header>
                                <Card.Body>
                                    <Row className="mb-3">
                                        <Col md={6}>
                                            <strong>Description (EN):</strong>
                                            <p>{vehicle.short_description_en || 'N/A'}</p>
                                        </Col>
                                        <Col md={6}>
                                            <strong>Description (AR):</strong>
                                            <p dir="rtl">{vehicle.short_description_ar || 'N/A'}</p>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Card className="mb-4">
                                <Card.Header>Media</Card.Header>
                                <Card.Body>
                                    {vehicle.main_image && (
                                        <div className="mb-3">
                                            <strong>Main Image:</strong>
                                            <img src={vehicle.main_image} alt="Main" className="img-fluid mt-2" style={{ maxHeight: '200px' }} />
                                        </div>
                                    )}
                                    {vehicle.video_url && (
                                        <div className="mb-2">
                                            <strong>Video URL:</strong>
                                            <a href={vehicle.video_url} target="_blank" rel="noopener noreferrer" className="ms-2">
                                                View Video
                                            </a>
                                        </div>
                                    )}
                                    {vehicle.brochure_url && (
                                        <div className="mb-2">
                                            <strong>Brochure:</strong>
                                            <a href={vehicle.brochure_url} target="_blank" rel="noopener noreferrer" className="ms-2">
                                                Download Brochure
                                            </a>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={6}>
                            <Card className="mb-4">
                                <Card.Header>Approval Information</Card.Header>
                                <Card.Body>
                                    <Row className="mb-2">
                                        <Col md={4}><strong>Created By:</strong></Col>
                                        <Col md={8}>{vehicle.creator?.name || 'N/A'}</Col>
                                    </Row>
                                    <Row className="mb-2">
                                        <Col md={4}><strong>Approved By:</strong></Col>
                                        <Col md={8}>{vehicle.approver?.name || 'N/A'}</Col>
                                    </Row>
                                    <Row className="mb-2">
                                        <Col md={4}><strong>Approved At:</strong></Col>
                                        <Col md={8}>{vehicle.approved_at || 'N/A'}</Col>
                                    </Row>
                                    {vehicle.approval_comment && (
                                        <Row className="mb-2">
                                            <Col md={4}><strong>Approval Comment:</strong></Col>
                                            <Col md={8}>{vehicle.approval_comment}</Col>
                                        </Row>
                                    )}
                                    {vehicle.rejection_reason && (
                                        <Row className="mb-2">
                                            <Col md={4}><strong>Rejection Reason:</strong></Col>
                                            <Col md={8} className="text-danger">{vehicle.rejection_reason}</Col>
                                        </Row>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Tab>

                <Tab eventKey="colors" title={`Colors (${vehicle.colors?.length || 0})`}>
                    <Card>
                        <Card.Body>
                            {vehicle.colors && vehicle.colors.length > 0 ? (
                                <Row>
                                    {vehicle.colors.map((color, index) => (
                                        <Col md={3} key={index} className="mb-3">
                                            <Card>
                                                <Card.Body className="text-center">
                                                    <div
                                                        style={{
                                                            width: '50px',
                                                            height: '50px',
                                                            backgroundColor: color.hex_code,
                                                            borderRadius: '50%',
                                                            margin: '0 auto 10px',
                                                            border: '1px solid #ddd'
                                                        }}
                                                    />
                                                    <div>{color.name}</div>
                                                    <small className="text-muted">{color.hex_code}</small>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                            ) : (
                                <p>No colors added yet.</p>
                            )}
                            <Link to={`/vehicle-management/${id}/colors`}>
                                <Button variant="primary" className="mt-3">Manage Colors</Button>
                            </Link>
                        </Card.Body>
                    </Card>
                </Tab>

                <Tab eventKey="variants" title={`Variants (${vehicle.variants?.length || 0})`}>
                    <Card>
                        <Card.Body>
                            {vehicle.variants && vehicle.variants.length > 0 ? (
                                <Row>
                                    {vehicle.variants.map((variant, index) => (
                                        <Col md={4} key={index} className="mb-3">
                                            <Card>
                                                <Card.Body>
                                                    <h6>{variant.name}</h6>
                                                    <p className="mb-1"><strong>Price:</strong> SAR {variant.price}</p>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                            ) : (
                                <p>No variants added yet.</p>
                            )}
                            <Link to={`/vehicle-management/${id}/variants`}>
                                <Button variant="primary" className="mt-3">Manage Variants</Button>
                            </Link>
                        </Card.Body>
                    </Card>
                </Tab>

            </Tabs>
        </Container>
    );
};

export default ViewVehicle;
